import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import { useCookies } from "react-cookie";
import { handleLogout } from "../../services/logoutService";
import Logo from "../Logo/Logo";
import { getCookie } from "../../utils/cookieUtils";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { withTranslations } from "../../hoc/withTranslations/withTranslations";

const LOCALE = "components_Navbar_Navbar";

const Navbar: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [, , removeCookie] = useCookies(["authToken"]);
  const jwtToken = getCookie("authToken");
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const threshold = 50;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      const isScrollingUp = prevScrollPos > currentScrollPos;

      if (isScrollingUp && currentScrollPos < threshold) {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        setTimeoutId(
          window.setTimeout(() => {
            setVisible(true);
          }, 300)
        );
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        setVisible(false);
      }

      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, [prevScrollPos, timeoutId, threshold]);

  const onLogout = async () => {
    setShowDropdown(false);
    await handleLogout(dispatch, removeCookie);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 transition-transform duration-300 bg-white shadow-sm z-50 ${
          visible ? "transform translate-y-0" : "transform -translate-y-full"
        }`}
        onMouseEnter={() => setVisible(true)}
      >
        <div className="flex justify-between items-center p-4">
          <a href="/" className="logo-container">
            <Logo className="h-12 w-auto ml-4" />
          </a>

          <div className="flex space-x-4 items-center">
            {!isAuthenticated || !jwtToken ? (
              <>
                <a
                  href="https://www.instagram.com/gift_nich/profilecard/"
                  className="text-primary hover:text-gray-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                  <span className="sr-only">{t("instagram")}</span>
                </a>
              </>
            ) : (
              <>
                <div className="relative">
                  <button
                    className="flex items-center space-x-2"
                    onClick={() => setShowDropdown(!showDropdown)}
                  >
                    {user?.photoUrl ? (
                      <img
                        src={user.photoUrl}
                        alt={user.name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-primary text-white flex items-center justify-center rounded-full">
                        {user?.name?.charAt(0) || "U"}
                      </div>
                    )}
                  </button>
                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white shadow-md rounded-md">
                      <button
                        onClick={onLogout}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        {t("logout")}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            <LanguageSwitcher />
          </div>
        </div>
      </nav>
      <div
        className={`transition-height duration-300 ${
          visible ? "h-[56px]" : "h-0"
        }`}
      ></div>
    </>
  );
};

export default withTranslations(LOCALE)(Navbar);
